// src/components/ImplantsList.js
import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

export default function ImplantsList({ onError, onSuccess }) {
  const [implants, setImplants]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [deleting, setDeleting]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listImplants();
      setImplants(data.implants || []);
    } catch (e) {
      onError(e.message);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete "${name}" from the live database?`)) return;
    setDeleting(name);
    try {
      await api.deleteImplant(name);
      onSuccess(`Deleted: ${name}`);
      setImplants(prev => prev.filter(i => i.implant_name !== name));
    } catch (e) {
      onError(e.message);
    } finally {
      setDeleting(null);
    }
  };

  const filtered = implants.filter(i =>
    search === '' ||
    i.implant_name.toLowerCase().includes(search.toLowerCase()) ||
    (i.manufacturer || '').toLowerCase().includes(search.toLowerCase())
  );

  const manufacturers = [...new Set(implants.map(i => i.manufacturer).filter(Boolean))].sort();

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Live Database</h1>
          <p style={s.sub}>{implants.length} implants indexed · used for patient-facing identification</p>
        </div>
        <button style={s.refreshBtn} onClick={load}>↻ Refresh</button>
      </div>

      <div style={s.toolbar}>
        <input
          style={s.search}
          placeholder="Search by name or manufacturer…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={s.mfrChips}>
          <button style={{ ...s.chip, ...(search === '' ? s.chipActive : {}) }}
            onClick={() => setSearch('')}>All</button>
          {manufacturers.map(m => (
            <button key={m}
              style={{ ...s.chip, ...(search === m ? s.chipActive : {}) }}
              onClick={() => setSearch(m)}>
              {m}
            </button>
          ))}
        </div>
      </div>

      {loading && <div style={s.empty}>Loading…</div>}

      {!loading && filtered.length === 0 && (
        <div style={s.empty}>No implants found{search ? ` for "${search}"` : ''}.</div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={s.table}>
          <div style={s.tableHeader}>
            <span style={s.col1}>Implant</span>
            <span style={s.col2}>Manufacturer</span>
            <span style={s.col3}>K Number</span>
            <span style={s.col4}>Added</span>
            <span style={s.col5}></span>
          </div>
          {filtered.map(imp => (
            <div key={imp.implant_name} style={s.tableRow}>
              <span style={{ ...s.col1, ...s.nameCell }}>{imp.implant_name}</span>
              <span style={{ ...s.col2, ...s.mfrCell }}>{imp.manufacturer || '—'}</span>
              <span style={{ ...s.col3, ...s.monoCell }}>{imp.k_number || '—'}</span>
              <span style={{ ...s.col4, ...s.dateCell }}>
                {imp.approved_at ? new Date(imp.approved_at).toLocaleDateString() : '—'}
              </span>
              <span style={s.col5}>
                <button
                  style={s.deleteBtn}
                  onClick={() => handleDelete(imp.implant_name)}
                  disabled={deleting === imp.implant_name}
                >
                  {deleting === imp.implant_name ? '…' : '✕'}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title:      { fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0, letterSpacing: '-0.03em' },
  sub:        { fontSize: 13, color: '#64748b', marginTop: 4 },
  refreshBtn: {
    padding: '8px 16px', background: '#1e2530', border: '1px solid #2d3748',
    borderRadius: 7, color: '#94a3b8', cursor: 'pointer', fontSize: 13,
    fontFamily: "'Inter', sans-serif",
  },
  toolbar: { marginBottom: 20 },
  search: {
    width: '100%', padding: '10px 14px', background: '#111827',
    border: '1px solid #2d3748', borderRadius: 8, color: '#e2e8f0',
    fontSize: 13, fontFamily: "'Inter', sans-serif", outline: 'none',
    marginBottom: 10, boxSizing: 'border-box',
  },
  mfrChips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: {
    fontSize: 11, padding: '4px 10px', borderRadius: 20,
    border: '1px solid #2d3748', background: '#0a0d14',
    color: '#64748b', cursor: 'pointer', fontFamily: "'Inter', sans-serif",
  },
  chipActive: { background: '#1e3a5f', border: '1px solid #1d4ed8', color: '#93c5fd' },
  empty: { color: '#475569', textAlign: 'center', padding: 60 },
  table:      { background: '#111827', borderRadius: 10, border: '1px solid #1e2530', overflow: 'hidden' },
  tableHeader: {
    display: 'flex', padding: '10px 16px', background: '#0a0d14',
    borderBottom: '1px solid #1e2530', fontSize: 10,
    color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em',
  },
  tableRow: {
    display: 'flex', padding: '12px 16px', borderBottom: '1px solid #1a2030',
    alignItems: 'center',
  },
  col1: { flex: 3 }, col2: { flex: 2 }, col3: { flex: 1.5 }, col4: { flex: 1.5 }, col5: { width: 40, textAlign: 'right' },
  nameCell: { fontSize: 13, fontWeight: 500, color: '#e2e8f0' },
  mfrCell:  { fontSize: 12, color: '#94a3b8' },
  monoCell: { fontSize: 11, color: '#60a5fa', fontFamily: "'IBM Plex Mono', monospace" },
  dateCell: { fontSize: 11, color: '#475569', fontFamily: "'IBM Plex Mono', monospace" },
  deleteBtn: {
    width: 26, height: 26, borderRadius: 5, border: '1px solid #2d3748',
    background: 'transparent', color: '#475569', cursor: 'pointer',
    fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};
